import { IPaginatedQuery } from '@monorepo/components';

import {
  ApiResponse,
  ApiResponseWithPagination,
  IAddDomainRateLimitRequest,
  IBotAnalyticsRequest,
  IBotStatusBulkUpdateRequest,
  ICreateChatBotRequest,
  IMessageAllocationRequest,
  ISendMessageRequest,
  IUpdateBotAppearanceRequest,
  IUpdateChatbotNameRequest,
  IUpdateTextPromptRequest,
  IVisibilityRequest,
} from '@/types';

import {
  IBotAnalyticsResponse,
  IBotImportStateResponse,
  IChatBot,
  IMessageAllocationSummary,
  IMessageResponse,
  IUserAnalyticsResponse,
} from '../../types/entities/chatbot.entities';
import http from '../http';

export const getAllChatBots = async (payload: IPaginatedQuery) => {
  const queryParams = new URLSearchParams({
    page: payload.page.toString(),
    limit: payload.limit.toString(),
    sortBy: payload.sortBy || '',
    sortOrder: payload.sortOrder || 'asc',
    search: payload.search || '',
  });

  if (payload.isPublic !== undefined) {
    queryParams.append('isPublic', payload.isPublic.toString());
  }

  if (payload.isActive !== undefined) {
    queryParams.append('isActive', payload.isActive.toString());
  }

  return http.get<ApiResponseWithPagination<IChatBot, 'docs'>>(`/chatbot?${queryParams}`);
};

export const addChatBot = async (payload: ICreateChatBotRequest) =>
  http.post<ApiResponse<{ chatbot: IChatBot }>>('/chatbot', payload);

export const sendMessage = async (id: string, payload: ISendMessageRequest) =>
  http.post<ApiResponse<{ response: IMessageResponse }>>(`/chatbot/${id}/message`, payload);

export const fetchBotById = async (id: string, customBaseUrl?: string) =>
  http.get<ApiResponse<{ chatbot: IChatBot }>>(`/chatbot/${id}`, {
    baseURL: customBaseUrl,
  });

export const updateBotAppearance = async (id: string, payload: IUpdateBotAppearanceRequest) =>
  http.patch<ApiResponse<{ chatbot: IChatBot }>>(`/chatbot/${id}/appearance`, payload);

export const fetchUserAnalytics = async () =>
  http.get<ApiResponse<IUserAnalyticsResponse>>('/chatbot/analytics/user');

/** Message/account widgets must not trigger document-library aggregation. */
export const fetchUserAccountSummary = async () =>
  http.get<ApiResponse<Omit<IUserAnalyticsResponse, 'trainingCharacters'>>>(
    '/chatbot/analytics/user/summary'
  );

export const fetchBotAnalytics = async (payload: IBotAnalyticsRequest) =>
  http.post<ApiResponse<IBotAnalyticsResponse>>('/chatbot/analytics', payload);

export const fetchBotImportState = async (id: string) =>
  http.get<ApiResponse<IBotImportStateResponse>>(`/chatbot/${id}/import-state`);

export const fetchMessageAllocations = async () =>
  http.get<ApiResponse<IMessageAllocationSummary>>('/chatbot/analytics/message-allocation');

export const addDomainRateLimit = async (id: string, payload: IAddDomainRateLimitRequest) =>
  http.patch<ApiResponse<IBotAnalyticsResponse>>(`/chatbot/${id}/domain`, payload);

export const fetchPublicBotById = async (
  id: string,
  applyValidation: boolean,
  customBaseUrl?: string
) =>
  http.get<ApiResponse<{ chatbot: IChatBot }>>(`/chatbot/public/${id}/${applyValidation}`, {
    baseURL: customBaseUrl,
  });

export const fetchChatbotAppearance = async (id: string, customBaseUrl?: string) =>
  http.get<ApiResponse<{ chatbot: Partial<IChatBot> }>>(`/chatbot/${id}/appearance`, {
    baseURL: customBaseUrl,
  });

export const fetchPrivateBotWithSession = async (
  id: string,
  sessionToken: string | null,
  customBaseUrl?: string
) =>
  http.get<ApiResponse<{ chatbot: IChatBot }>>(`/chatbot/${id}/with-session`, {
    baseURL: customBaseUrl,
    ...(sessionToken && {
      headers: {
        'x-private-bot-session': sessionToken,
      },
    }),
  });

export const sendPublicMessage = async (id: string, payload: ISendMessageRequest) =>
  http.post<ApiResponse<{ response: IMessageResponse }>>(`/chatbot/public/${id}/message`, payload);

export const chatbotVisibilityBulkUpdate = async (payload: IVisibilityRequest) =>
  http.patch<ApiResponse<{ chatbot: IChatBot }>>('/chatbot/bulk/visibility', payload);

export const chatbotStatusBulkUpdate = async (payload: IBotStatusBulkUpdateRequest) =>
  http.patch<ApiResponse<{ chatbot: IChatBot }>>('/chatbot/bulk/status', payload);

// REQ-231: DELETE /chatbot/:id now requires a body with `typedChatbotName`
// (server-side re-validates against `chatbot.name` — defence-in-depth against
// callers that bypass the UI modal). Optional `reason` is surfaced to the
// Owner's approval email when the caller is a delegated Admin.
//
// Response shape varies by caller role:
//   - Owner/SuperAdmin → `{ chatbot }` (immediate soft-delete)
//   - Admin w/ canDeleteChatbots → `{ chatbot, deletionRequest }` (pending)
export interface IDeleteChatbotBody {
  typedChatbotName: string;
  reason?: string;
}

export interface IDeletionRequestSummary {
  id: string;
  status: 'PENDING' | 'APPROVED' | 'DENIED' | 'AUTO_CANCELLED' | 'COMPLETED';
  expiresAt: string;
}

export const deleteChatbotById = async (id: string, payload: IDeleteChatbotBody) =>
  http.delete<ApiResponse<{ chatbot: IChatBot; deletionRequest?: IDeletionRequestSummary }>>(
    `/chatbot/${id}`,
    payload
  );

export const updateTextPrompt = async (id: string, payload: IUpdateTextPromptRequest) =>
  http.patch<ApiResponse<{ chatbot: IChatBot }>>(`/chatbot/${id}/text-prompt`, payload);

export const updateChatLimit = async (id: string, payload: IMessageAllocationRequest) =>
  http.patch<ApiResponse<{ chatbot: IChatBot }>>(`/chatbot/${id}/chat-limit`, payload);

export const updateChatbotName = async (id: string, payload: IUpdateChatbotNameRequest) =>
  http.patch<ApiResponse<{ chatbot: IChatBot }>>(`/chatbot/${id}/name`, payload);

// Assigned bots for TEAM_MEMBER users (includes permissions for staff)
export const getAssignedBots = async () =>
  http.get<
    ApiResponse<{
      items: (Pick<IChatBot, 'id' | 'name' | 'createdAt' | 'updatedAt'> & {
        permissions?: import('@/types/entities/chatbot.entities').ChatbotPermissions;
        owner?: { name: string; email: string };
      })[];
    }>
  >('/chatbot/assigned');
